"use client";

// The "right now" line for TODAY's Rahu Kaal: on now, starts in, or passed.
//
// Rule 25 discipline (the same contract `LiveTimingCountdown` and
// `PanchangNowStrip` keep): the only client arithmetic is comparing Date.now()
// against the engine's own `starts_at`/`ends_at` instants. The `start`/`end`
// "HH:MM" strings are display projections and are never turned into moments
// here. A payload without the instants (an older cached response) renders no
// line at all.
//
// Hydration: the server HTML and the first client render carry only the
// reserved, empty container (the clock's server snapshot is null); the sentence
// appears after mount, so a cached HTML document served minutes later can never
// disagree with its hydration. The server reserves the box only for the page's
// own today (`today`), so no other date's layout shifts.

import { useSyncExternalStore } from "react";

import type { MuhuratResponse } from "@/lib/api/muhurat";

export type RahuKaalNowState =
  { kind: "on"; remainingMs: number } | { kind: "next"; remainingMs: number } | { kind: "passed" };

type Window = Pick<MuhuratResponse["rahu_kaal"], "starts_at" | "ends_at">;

function instants(window: Window): { start: number; end: number } | null {
  if (!window.starts_at || !window.ends_at) return null;
  const start = Date.parse(window.starts_at);
  const end = Date.parse(window.ends_at);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  return { start, end };
}

/**
 * The civil date (YYYY-MM-DD) at `nowMs` in an IANA zone, or null if the zone is unknown.
 * @internal
 */
export function civilDateIn(timeZone: string, nowMs: number): string | null {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(nowMs));
  } catch {
    return null;
  }
}

/**
 * Whether `result` is today's window for its own city and carries countdown instants.
 * @internal
 */
export function isTodaysRahuKaal(
  result: Pick<MuhuratResponse, "date" | "timezone" | "rahu_kaal">,
  nowMs: number
): boolean {
  return instants(result.rahu_kaal) !== null && civilDateIn(result.timezone, nowMs) === result.date;
}

/**
 * Where `nowMs` falls against the engine's Rahu Kaal instants.
 * @internal
 */
export function rahuKaalNowState(window: Window, nowMs: number): RahuKaalNowState | null {
  const range = instants(window);
  if (!range) return null;
  if (nowMs < range.start) return { kind: "next", remainingMs: range.start - nowMs };
  if (nowMs < range.end) return { kind: "on", remainingMs: range.end - nowMs };
  return { kind: "passed" };
}

/**
 * Remaining time as HH:MM, rounded up so a running window never reads 00:00.
 * @internal
 */
export function formatHoursMinutes(remainingMs: number): string {
  const totalMinutes = Math.max(1, Math.ceil(remainingMs / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

const COPY = {
  en: {
    on: (clock: string) => ["Rahu Kaal is on now — ends in", clock, ""] as const,
    next: (clock: string) => ["Next Rahu Kaal starts in", clock, ""] as const,
    passed: "Rahu Kaal today has passed",
  },
  hi: {
    on: (clock: string) => ["राहु काल अभी चल रहा है —", clock, "में समाप्त"] as const,
    next: (clock: string) => ["अगला राहु काल", clock, "में शुरू होगा"] as const,
    passed: "आज का राहु काल बीत चुका है",
  },
} as const;

// One shared one-second clock for every mounted line. `null` is the server (and
// hydration) snapshot, so the first client render matches the server HTML.
let clockMs: number | null = null;
let clockTimer: ReturnType<typeof setInterval> | null = null;
const clockListeners = new Set<() => void>();

function subscribeClock(listener: () => void): () => void {
  clockListeners.add(listener);
  if (clockTimer === null) {
    clockMs = Date.now();
    clockTimer = setInterval(() => {
      clockMs = Date.now();
      clockListeners.forEach((notify) => notify());
    }, 1_000);
  }
  return () => {
    clockListeners.delete(listener);
    if (clockListeners.size === 0 && clockTimer !== null) {
      clearInterval(clockTimer);
      clockTimer = null;
      clockMs = null;
    }
  };
}

const readClock = () => clockMs;
const readServerClock = () => null;

export default function RahuKaalNow({
  result,
  today,
  language = "en",
}: {
  result: Pick<MuhuratResponse, "date" | "timezone" | "rahu_kaal">;
  /**
   * The city's civil date when the page was server-rendered. It decides only
   * whether the line's space is reserved in the server HTML; the mounted clock
   * then re-checks against the engine's own zone. Omitted, nothing is reserved.
   */
  today?: string;
  language?: "en" | "hi";
}) {
  const nowMs = useSyncExternalStore(subscribeClock, readClock, readServerClock);

  if (nowMs === null) {
    // Server HTML and hydration: an empty, reserved box for today's window only.
    if (today === undefined || today !== result.date || !instants(result.rahu_kaal)) return null;
    return <div className="min-h-[52px]" data-rahu-kaal-now="" />;
  }
  if (!isTodaysRahuKaal(result, nowMs)) return null;

  const state = rahuKaalNowState(result.rahu_kaal, nowMs);
  if (!state) return null;
  const copy = COPY[language];
  const tone =
    state.kind === "on"
      ? "border-error/35 bg-error/10 text-error"
      : "border-border-subtle bg-surface-warm text-text-primary";

  return (
    <div className="min-h-[52px]" data-rahu-kaal-now={state.kind}>
      <p
        className={`flex min-h-[52px] flex-wrap items-center justify-center gap-1.5 rounded-xl border px-4 py-3 text-center text-body font-semibold ${tone}`}
      >
        {state.kind === "passed" ? (
          copy.passed
        ) : (
          <RemainingLine
            parts={(state.kind === "on" ? copy.on : copy.next)(
              formatHoursMinutes(state.remainingMs)
            )}
          />
        )}
      </p>
    </div>
  );
}

function RemainingLine({ parts }: { parts: readonly [string, string, string] }) {
  const [before, clock, after] = parts;
  return (
    <>
      <span>{before}</span>{" "}
      <span className="font-mono font-bold tabular-nums tracking-wider">{clock}</span>
      {after ? (
        <>
          {" "}
          <span>{after}</span>
        </>
      ) : null}
    </>
  );
}
